/* @flow */
/* eslint-env es6,browser */

import * as u from './utils';

/**
 * @module usersync
 */
/**
 * @description Provides functions to fire user sync pixels/iframes
 */

class _userSync {
  _queue: Array<syncObj>;
  _config: syncData;
  _defConfig: syncData;
  _syncContainerId: string;
  _syncCountPerBidder: Object;
  _processor: Object;
  constructor() {
    this._queue = [];
    this._defConfig = {
      delay: 1000,
      syncsPerBidder: 5, //number of syncs per bidder
      enableUserSync: false, //global config to disable user sync feature
      allowedSyncTypes: []
    };
    this._syncCountPerBidder = {};
    this._syncContainerId = `sf-${u.getRandom()}`;
    this._processor = {
      image: this._fireImagePixel,
      iframe: this._loadIframe
    };
  }

  /**
   * Update user sync module settings from config
   * @param {syncData} settings - config settings for user sync
   * @returns {undefined}
   */
  updateSettings(settings: syncData) {
    settings = settings || {};
    this._config = u.extend(true, {}, this._defConfig, settings);
  }

  /**
   * Collects list of user sync objects from all the ative bidders
   * @param {Array<hbPartnerBase>} bidders - List of bidders
   * @returns {undefined}
   */
  handleUserSync(bidders: Array<hbPartnerBase>) {
    //check if usersync is enabled
    if (!this.isUserSyncEnabled()) {
      return;
    }
    // $FlowFixMe
    const syncObjects: Array<syncObj> = bidders
      .map(b => b.getUserSync())
      .flatten()
      .filter(
        e =>
          Object.keys(e).length &&
          e.type &&
          e.type.length &&
          e.url &&
          e.url.length
      );
    if (syncObjects.length) {
      this._queueSyncs(syncObjects);
    }
  }

  /**
   * Returns if user sync global feature is enabled
   * @returns {boolean} - if user sync is enabled
   */

  isUserSyncEnabled() {
    return this._config.enableUserSync;
  }

  /**
   * Adds all sync objects to queue bases on the type of sync
   * @param {Array<Object>} syncRequests - List of sync objects
   * @returns {undefined}
   * @access private
   */
  _queueSyncs(syncRequests: Array<syncObj>) {
    const supportedUS = Object.keys(this._processor),
      allowedUS = u.intersect(supportedUS, this._config.allowedSyncTypes);

    //Add only supported & enabled sync types to the queue
    //Check if sync per bidder count is reached as well
    this._queue = syncRequests
      .filter(e => {
        const typeSupported = allowedUS.includes(e.type);
        if (!typeSupported) {
          u.logDebug(`UserSync type ${e.type} is not allowed for ${e.name}`);
        }
        return typeSupported;
      })
      .filter(e => {
        let cnt = this._syncCountPerBidder[e.name] || 0,
          allowed = cnt < this._config.syncsPerBidder;
        if (!allowed) {
          u.logDebug(`UserSync count reached limit for ${e.name}`);
        } else {
          this._syncCountPerBidder[e.name] = cnt + 1;
        }
        return allowed;
      });
    if (this._queue.length) {
      setTimeout(() => this.fireSync(), this._config.delay);
    }
  }

  /**
   * Creates sync container if it does not exists and cleans it
   * @returns {HTMLElement} - Div container for sync frames and pixels
   * @access private
   */
  _createAndCleanContainer(): HTMLElement {
    let syncDiv: HTMLElement = ((window.document.getElementById(
      this._syncContainerId
    ): any): HTMLElement);
    if (!syncDiv) {
      syncDiv = window.document.createElement('div');
      syncDiv.setAttribute('id', this._syncContainerId);
      window.document.body.appendChild(syncDiv);
    }
    u.cleanFrame(window.document, syncDiv, '');
    return syncDiv;
  }

  /**
   * Gets list of user sync requests and fires them after some delay
   * @returns {undefined}
   */
  fireSync() {
    try {
      //empty and re-initialize container div
      const containerDiv = this._createAndCleanContainer();
      this._queue.forEach(sync => {
        const proc = this._processor[sync.type];
        proc.call(this, containerDiv, sync);
      });
    } catch (error) {
      u.logError(`Error firing user syncs: ${error}`);
    }

    this._queue.length = 0;
  }

  /**
   *
   * Fire image pixel for the given sync object
   * @param {HTMLElement} containerDiv Sync Object Container div
   * @param {syncObj} sync Sync Object
   * @memberof _userSync
   * @returns {undefined}
   */
  _fireImagePixel(containerDiv: HTMLElement, sync: syncObj) {
    const { name, url } = sync;
    const img = new Image();
    img.src = url;
    containerDiv.appendChild(img);
    u.logDebug(`Invoking image pixel user sync for bidder: ${name}`);
  }

  /**
   *
   * Fire iframe usersync request
   * @param {HTMLElement} containerDiv Sync Object Container div
   * @param {syncObj} sync Sync Object
   * @memberof _userSync
   * @returns {undefined}
   */
  _loadIframe(containerDiv: HTMLElement, sync: syncObj) {
    const { name, url } = sync;
    const iframeID = `${name}-sf-${u.getRandom()}`;
    let newIf = u.createIframe(window.document, iframeID, 0, 0, url);
    newIf.setAttribute('display', 'none');
    containerDiv.appendChild(newIf);
    u.logDebug(`Invoking iframe user sync for bidder: ${name}`);
  }
}

export default new _userSync();
